<template>
  <div>
    <transition name="header-fade">
      <div
        v-show="showHeader"
        :class="['header-fixed', { 'shadow-lg': isFixed }]"
        style="width: 100%; left: 0; right: 0; top: 0; z-index: 100; position: fixed; transition: opacity 0.3s cubic-bezier(.4,0,.2,1);"
        v-if="isFixed"
      >
        <PreorderBar />
        <header class="relative w-full bg-white/95 text-black backdrop-blur-sm border-b border-gray-200 z-50 select-none">
          <nav class="px-4 md:px-2 lg:container lg:mx-auto py-4 text-xs">
            <!-- Desktop Navigation -->
            <div class="hidden md:grid md:grid-cols-3 items-center">
              <!-- Liens à gauche -->
              <div class="flex items-center space-x-6">
                <NuxtLink :to="localizedPaths.boots" class="text-gray-600 hover:text-black select-none">{{ $t('nav.boots') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.ankleBoots" class="text-gray-600 hover:text-black whitespace-nowrap select-none">{{ $t('nav.ankleBoots') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.pumps" class="text-gray-600 hover:text-black select-none">{{ $t('nav.pumps') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.sandals" class="text-gray-600 hover:text-black select-none">{{ $t('nav.sandals') }}</NuxtLink>
                <NuxtLink 
                  v-if="user" 
                  :to="localizedPaths.wishlist" 
                  :aria-label="$t('account.wishlist.title')"
                  class="text-gray-600 hover:text-black select-none relative"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :fill="wishlistCount > 0 ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <ClientOnly>
                    <span v-if="wishlistCount > 0" class="absolute -top-2 left-3 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {{ wishlistCount }}
                    </span>
                  </ClientOnly>
                </NuxtLink>
                <NuxtLink 
                  v-if="user" 
                  :to="localizedPaths.profile" 
                  :aria-label="$t('account.profile.title')"
                  class="text-gray-600  hover:text-black select-none -ml-4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </NuxtLink>
              </div>
              <div class="flex justify-center">
                <NuxtLink
                  :to="localizedPaths.home"
                  aria-label="Kendrick home"
                  class="inline-flex items-center justify-center"
                >
                  <NuxtImg
                    :key="headerLogoKey"
                    :src="headerLogoSrc"
                    alt="Kendrick"
                    class="block w-[3.2rem] min-[900px]:w-[3.9rem] h-auto opacity-90 transition hover:opacity-100"
                    width="60"
                    height="48"
                    loading="eager"
                    decoding="async" provider="passthrough" sizes="60px" />
                </NuxtLink>
              </div>
              <!-- Liens à droite -->
              <div class="flex items-center justify-end space-x-6">
                <NuxtLink :to="localizedPaths.magazine" class="text-gray-600 hover:text-black select-none">{{ $t('nav.magazine') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.brand" class="text-gray-600 hover:text-black select-none">{{ $t('nav.brand') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.fabrication" class="text-gray-600 hover:text-black select-none">{{ $t('nav.fabrication') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.comfort" class="text-gray-600 hover:text-black select-none">{{ $t('nav.comfort') }}</NuxtLink>
                <NuxtLink :to="localizedPaths.store" class="text-gray-600 hover:text-black select-none">{{ $t('nav.store') }}</NuxtLink>
                <LanguageSwitcher />
                <a href="https://kendrick.paris/s/sig" target="_blank" rel="noopener" aria-label="Instagram" class="text-gray-600 hover:text-black select-none" @click="handleSocialClick('instagram', 'header')">
                  <svg xmlns="http://www.w3.org/2000/svg" class="text-lg lg:text-2xl" viewBox="0 0 448 512" fill="currentColor" aria-hidden="true">
                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                  </svg>
                </a>
              </div>
            </div>
            
            <!-- Mobile Navigation -->
            <div class="md:hidden flex items-center justify-between">
              <div class="flex justify-start">
                <NuxtLink
                  :to="localizedPaths.home"
                  aria-label="Kendrick home"
                  class="inline-flex items-center justify-center"
                >
                  <NuxtImg
                    :key="`mobile-${headerLogoKey}`"
                    :src="headerLogoSrc"
                    alt="Kendrick"
                    class="block w-[3.2rem] min-[900px]:w-[3.9rem] h-auto opacity-90"
                    width="60"
                    height="48"
                    loading="eager"
                    decoding="async" provider="passthrough" sizes="60px" />
                </NuxtLink>
              </div>
              <div class="flex justify-end">
                <button
                  @click="toggleMobileMenu"
                  class="p-2 text-gray-600 hover:text-black transition-colors select-none z-[120] relative"
                  aria-label="Toggle mobile menu"
                >
                  <div class="w-6 h-6 flex flex-col justify-center items-center">
                    <span 
                      class="block w-5 h-0.5 bg-current transition-all duration-300"
                      :class="{ 'rotate-45 translate-y-1': isMobileMenuOpen }"
                    ></span>
                    <span 
                      class="block w-5 h-0.5 bg-current mt-1 transition-all duration-300"
                      :class="{ 'opacity-0': isMobileMenuOpen }"
                    ></span>
                    <span 
                      class="block w-5 h-0.5 bg-current mt-1 transition-all duration-300"
                      :class="{ '-rotate-45 -translate-y-1': isMobileMenuOpen }"
                    ></span>
                  </div>
                </button>
              </div>
            </div>
          </nav>
        </header>
        <SubHeader />
      </div>
    </transition>
    
    <div v-if="!isFixed">
      <PreorderBar />
      <header class="relative w-full bg-white/95 text-black backdrop-blur-sm border-b border-gray-200 z-50 select-none">
        <nav class="px-4 md:px-4 lg:container lg:mx-auto py-4 text-xs">
          <!-- Desktop Navigation -->
          <div class="hidden md:grid md:grid-cols-3 items-center">
            <!-- Liens à gauche -->
            <div class="flex items-center space-x-6">
              <NuxtLink :to="localizedPaths.boots" class="text-gray-600 hover:text-black select-none">{{ $t('nav.boots') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.ankleBoots" class="text-gray-600 hover:text-black whitespace-nowrap select-none">{{ $t('nav.ankleBoots') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.pumps" class="text-gray-600 hover:text-black select-none">{{ $t('nav.pumps') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.sandals" class="text-gray-600 hover:text-black select-none">{{ $t('nav.sandals') }}</NuxtLink>
              <NuxtLink 
                v-if="user" 
                :to="localizedPaths.wishlist" 
                :aria-label="$t('account.wishlist.title')"
                class="text-gray-600 hover:text-black select-none relative"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :fill="wishlistCount > 0 ? 'currentColor' : 'none'" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <ClientOnly>
                  <span v-if="wishlistCount > 0" class="absolute -top-2 left-3 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {{ wishlistCount }}
                  </span>
                </ClientOnly>
              </NuxtLink>
              <NuxtLink 
                v-if="user" 
                :to="localizedPaths.profile" 
                :aria-label="$t('account.profile.title')"
                class="text-gray-600 hover:text-black select-none -ml-4"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </NuxtLink>
            </div>
            <div class="flex justify-center">
              <NuxtLink
                :to="localizedPaths.home"
                aria-label="Kendrick home"
                class="inline-flex items-center justify-center"
              >
                <NuxtImg
                  :key="`static-${headerLogoKey}`"
                  :src="headerLogoSrc"
                  alt="Kendrick"
                  class="block w-[3.2rem] min-[900px]:w-[3.9rem] h-auto opacity-90 transition hover:opacity-100"
                  width="60"
                  height="48"
                  loading="eager"
                  decoding="async" provider="passthrough" sizes="60px" />
              </NuxtLink>
            </div>
            <!-- Liens à droite -->
            <div class="flex items-center justify-end space-x-6">
              <NuxtLink :to="localizedPaths.magazine" class="text-gray-600 hover:text-black select-none">{{ $t('nav.magazine') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.brand" class="text-gray-600 hover:text-black select-none">{{ $t('nav.brand') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.fabrication" class="text-gray-600 hover:text-black select-none">{{ $t('nav.fabrication') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.comfort" class="text-gray-600 hover:text-black select-none">{{ $t('nav.comfort') }}</NuxtLink>
              <NuxtLink :to="localizedPaths.store" class="text-gray-600 hover:text-black select-none">{{ $t('nav.store') }}</NuxtLink>
              <LanguageSwitcher />
              <a href="https://kendrick.paris/s/sig" target="_blank" rel="noopener" aria-label="Instagram" class="text-gray-600 hover:text-black select-none" @click="handleSocialClick('instagram', 'header')">
                <svg xmlns="http://www.w3.org/2000/svg" class="text-lg lg:text-2xl" viewBox="0 0 448 512" fill="currentColor" aria-hidden="true">
                  <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                </svg>
              </a>
            </div>
          </div>
          
          <!-- Mobile Navigation -->
          <div class="md:hidden flex items-center justify-between">
            <div class="flex justify-start">
              <NuxtLink
                :to="localizedPaths.home"
                aria-label="Kendrick home"
                class="inline-flex items-center justify-center"
              >
                <NuxtImg
                  :key="`mobile-static-${headerLogoKey}`"
                  :src="headerLogoSrc"
                  alt="Kendrick"
                  class="block w-[3.2rem] min-[900px]:w-[3.9rem] h-auto opacity-90"
                  width="60"
                  height="48"
                  loading="eager"
                  decoding="async" provider="passthrough" sizes="60px" />
              </NuxtLink>
            </div>
            <div class="flex justify-end">
              <button
                @click="toggleMobileMenu"
                class="p-2 text-gray-600 hover:text-black transition-colors select-none z-[120] relative"
                aria-label="Toggle mobile menu"
              >
                <div class="w-6 h-6 flex flex-col justify-center items-center">
                  <span 
                    class="block w-5 h-0.5 bg-current transition-all duration-300"
                    :class="{ 'rotate-45 translate-y-1': isMobileMenuOpen }"
                  ></span>
                  <span 
                    class="block w-5 h-0.5 bg-current mt-1 transition-all duration-300"
                    :class="{ 'opacity-0': isMobileMenuOpen }"
                  ></span>
                  <span 
                    class="block w-5 h-0.5 bg-current mt-1 transition-all duration-300"
                    :class="{ '-rotate-45 -translate-y-1': isMobileMenuOpen }"
                  ></span>
                </div>
              </button>
            </div>
          </div>
        </nav>
      </header>
      <SubHeader />
    </div>
    
    <!-- Mobile Menu Overlay -->
    <transition name="mobile-menu">
      <div 
        v-if="isMobileMenuOpen" 
        class="fixed inset-0 bg-black bg-opacity-50 z-[125] md:hidden"
        @click="closeMobileMenu"
      ></div>
    </transition>
    
    <!-- Mobile Menu Panel -->
    <transition name="mobile-menu-panel">
      <div 
        v-if="isMobileMenuOpen" 
        class="fixed top-0 right-0 h-full w-[70%] bg-white shadow-xl z-[130] md:hidden"
      >
        <div class="p-6">
          <!-- Close Button -->
          <div class="flex justify-end mb-4">
            <button
              @click="closeMobileMenu"
              class="p-2 text-gray-600 hover:text-black transition-colors select-none"
              aria-label="Close mobile menu"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          
          <!-- Mobile Menu Items -->
          <nav class="space-y-4">
            <!-- Products Section -->
            <div>
              <h3 class="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wider">{{ $t('footer.heels') }}</h3>
              <div class="space-y-1">
                <NuxtLink 
                  :to="localizedPaths.boots" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.boots') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.ankleBoots" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.ankleBoots') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.pumps" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.pumps') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.sandals" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.sandals') }}
                </NuxtLink>
                <NuxtLink 
                  v-if="user"
                  :to="localizedPaths.wishlist" 
                  @click="closeMobileMenu"
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none relative w-full text-left"
                >
                  {{ $t('account.wishlist.title') }}
                  <ClientOnly>
                    <span v-if="wishlistCount > 0" class="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                      {{ wishlistCount }}
                    </span>
                  </ClientOnly>
                </NuxtLink>
                <NuxtLink 
                  v-if="user"
                  :to="localizedPaths.profile" 
                  @click="closeMobileMenu"
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none w-full text-left"
                >
                  {{ $t('account.profile.title') }}
                </NuxtLink>
              
              </div>
            </div>
            
            <!-- Other Links Section -->
            <div>
              <h3 class="text-sm font-semibold text-gray-900 mb-2 uppercase tracking-wider">{{ $t('nav.brand') }}</h3>
              <div class="space-y-1">
                <NuxtLink
                  :to="localizedPaths.magazine"
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.magazine') }}
                </NuxtLink>
                <NuxtLink
                  :to="localizedPaths.brand"
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.brand') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.fabrication" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.fabrication') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.comfort" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.comfort') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.press" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('page.medias.title') }}
                </NuxtLink>
                <NuxtLink 
                  :to="localizedPaths.store" 
                  class="block text-base text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu"
                >
                  {{ $t('nav.store') }}
                </NuxtLink>
              </div>
            </div>
            
            <!-- Language Switcher & Social Links -->
            <div class="pt-4 border-t border-gray-200">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wider">{{ $t('common.language') }}</h3>
              <div class="flex items-center justify-between">
                <LanguageSwitcher />
                <a 
                  href="https://kendrick.paris/s/sig"
                  target="_blank" 
                  rel="noopener" 
                  aria-label="Instagram"
                  class="text-gray-600 hover:text-black transition-colors select-none"
                  @click="closeMobileMenu(); handleSocialClick('instagram', 'mobile_menu')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="text-4xl" viewBox="0 0 448 512" fill="currentColor" aria-hidden="true">
                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                  </svg>
                </a>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import PreorderBar from './PreorderBar.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'
import SubHeader from './SubHeader.vue'
import { useLocalizedRoute } from '~/composables/useLocalizedRoute'
import { useSiteLocale } from '~/composables/useSiteLocale'

const route = useRoute()
const { getLocalizedPath } = useLocalizedRoute()
const currentLocale = useSiteLocale()
const supabase = ref(null)
const user = ref(null)
const wishlistCount = ref(0)
const headerLogoSrc = '/brand-assets/kendrick_logo.svg?v=20260326'
const headerLogoKey = computed(() => route.fullPath || 'header-logo')

const handleSocialClick = async (platform, location) => {
  if (!process.client) return
  const { useGA } = await import('~/composables/useGA')
  useGA().trackSocialClick(platform, location)
}

// Create computed properties for localized paths
const localizedPaths = computed(() => ({
  boots: getLocalizedPath('/boots'),
  ankleBoots: getLocalizedPath('/ankle-boots'),
  pumps: getLocalizedPath('/pumps'),
  sandals: getLocalizedPath('/sandals'),
  magazine: currentLocale.value === 'fr' ? '/fr/magazine' : '/magazine',
  brand: getLocalizedPath('/brand'),
  fabrication: getLocalizedPath('/fabrication'),
  comfort: getLocalizedPath('/comfort'),
  store: getLocalizedPath('/store'),
  gloves: getLocalizedPath('/gloves'),
  press: getLocalizedPath('/press'),
  contact: getLocalizedPath('/contact'),
  home: getLocalizedPath('/'),
  wishlist: getLocalizedPath('/account/wishlist'),
  profile: getLocalizedPath('/account/profile')
}))

// Fetch wishlist count
const fetchWishlistCount = async () => {
  if (!user.value || !supabase.value) {
    wishlistCount.value = 0
    return
  }

  try {
    const { count, error } = await supabase.value
      .from('wishlist')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.value.id)

    if (error) {
      console.error('Error fetching wishlist count:', error)
    } else {
      wishlistCount.value = count || 0
    }
  } catch (error) {
    console.error('Error fetching wishlist count:', error)
  }
}

// Subscribe to wishlist changes
let wishlistChannel = null
const setupWishlistSubscription = () => {
  if (!user.value || !process.client || !supabase.value) return

  // Remove existing channel if any
  if (wishlistChannel) {
    supabase.value.removeChannel(wishlistChannel)
    wishlistChannel = null
  }

  wishlistChannel = supabase.value
    .channel('header-wishlist')
    .on(
      'postgres_changes',
      { 
        event: '*', 
        schema: 'public', 
        table: 'wishlist',
        filter: `user_id=eq.${user.value.id}`
      },
      () => {
        fetchWishlistCount()
      }
    )
    .subscribe()
}

// Watch for user changes and wishlist updates
const handleUserChange = async (newUser) => {
  if (newUser) {
    await fetchWishlistCount()
    if (process.client) {
      setupWishlistSubscription()
    }
  } else {
    wishlistCount.value = 0
    if (wishlistChannel) {
      if (supabase.value) {
        supabase.value.removeChannel(wishlistChannel)
      }
      wishlistChannel = null
    }
  }
}

const isFixed = ref(false)
const showHeader = ref(true)
const isMobileMenuOpen = ref(false)
let lastScroll = 0
let ticking = false
let deferredInitHandle = null
let deferredInitTimer = null

// Mobile menu functions
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
  // Prevent body scroll when menu is open
  if (isMobileMenuOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false
  document.body.style.overflow = ''
}

function onScroll() {
  const current = window.scrollY
  const viewportHeight = window.innerHeight
  
  if (!ticking) {
    window.requestAnimationFrame(() => {
      // If viewport height is less than 120px, always keep header fixed but allow show/hide
      if (viewportHeight < 120) {
        isFixed.value = true
        if (current > 120) {
          if (current < lastScroll) {
            // Scrolling up
            showHeader.value = true
          } else {
            // Scrolling down
            showHeader.value = false
          }
        } else {
          showHeader.value = true
        }
      } else if (current > 120) {
        if (current < lastScroll) {
          // Scrolling up
          showHeader.value = true
          isFixed.value = true
        } else {
          // Scrolling down
          showHeader.value = false
        }
      } else {
        isFixed.value = false
        showHeader.value = true
      }
      lastScroll = current
      ticking = false
    })
    ticking = true
  }
}

onMounted(() => {
  // Check viewport height on mount and set initial state
  const viewportHeight = window.innerHeight
  if (viewportHeight < 120) {
    isFixed.value = true
    showHeader.value = true
  }
  
  window.addEventListener('scroll', onScroll, { passive: true })

  const initDeferredHeaderFeatures = async () => {
    // Keep wishlist bootstrapping out of the initial critical path.
    const { useSupabaseClient, useSupabaseUser } = await import('~/composables/useSupabaseClient')
    supabase.value = useSupabaseClient()
    const supabaseUser = useSupabaseUser()
    watch(supabaseUser, handleUserChange, { immediate: true })
  }

  if ('requestIdleCallback' in window) {
    deferredInitHandle = window.requestIdleCallback(() => {
      initDeferredHeaderFeatures()
    }, { timeout: 2000 })
  } else {
    deferredInitTimer = setTimeout(() => {
      initDeferredHeaderFeatures()
    }, 1200)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll)
  if (deferredInitHandle && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(deferredInitHandle)
    deferredInitHandle = null
  }
  if (deferredInitTimer) {
    clearTimeout(deferredInitTimer)
    deferredInitTimer = null
  }
  
  // Clean up wishlist subscription
  if (wishlistChannel && supabase.value) {
    supabase.value.removeChannel(wishlistChannel)
    wishlistChannel = null
  }
})
</script>

<style scoped>
.header-fixed {
  box-shadow: 0 2px 8px 0 rgba(0,0,0,0.04);
  background: white;
}
.header-fade-enter-active, .header-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(.4,0,.2,1);
}
.header-fade-enter-from, .header-fade-leave-to {
  opacity: 0;
}
.header-fade-enter-to, .header-fade-leave-from {
  opacity: 1;
}

/* Mobile menu animations */
.mobile-menu-enter-active, .mobile-menu-leave-active {
  transition: opacity 0.3s ease;
}
.mobile-menu-enter-from, .mobile-menu-leave-to {
  opacity: 0;
}

.mobile-menu-panel-enter-active, .mobile-menu-panel-leave-active {
  transition: transform 0.3s ease;
}
.mobile-menu-panel-enter-from, .mobile-menu-panel-leave-to {
  transform: translateX(100%);
}
</style> 
